import { Button, Tooltip } from '@mui/material';
import { GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarFilterButton } from '@mui/x-data-grid';
import { isAxiosError } from 'axios';
import { format, isToday } from 'date-fns';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ArrowRepeatIcon16pxSvg from '../../../assets/images/icons/ArrowRepeatIcon16pxSvg';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';
import EditNew20pxSvg from '../../../assets/images/icons/EditNew20pxSvg';
import DeleteIcon16pxSvg from '../../../assets/images/icons/DeleteIcon16pxSvg';
import InfoIcon16pxSvg from '../../../assets/images/icons/InfoIcon16pxSvg';
import { getRequestList } from '../../../axios/Eventlog/EventLogApi';
import { updateNotification } from '../../../axios/Notification/NotificationApi';
import { StyledDataGrid } from '../../../components/StyledComponents';
import { useAuth } from '../../../context/AuthContext';
import { useSettings } from '../../../context/SettingsContext';
import { useNotification } from '../../../context/NotificationContext';
import { generateTranslateMuiTable } from '../../../utils/Function/commonFunction';
import CustomGridToolbarQuickFilter from '../CustomGridToolbarQuickFilter';
import CustomPagination from '../CustomPagination';
import { formatDateTimeString } from './../../../utils/FormatDateTimeString/dateUtils';
import Cookies from "js-cookie";
import dayjs from 'dayjs';
import { getUserList } from '../../../axios/User/UserApi';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';
import EditRequestDialogByUser from './EditRequestDialogByUser';
import EditRequestEventDialogByUser from './EditRequestEventDialogByUser';
import DetailRequestDialog from './DetailRequestDialog';
import DetailRequestDialogAR from './DetailRequestDialogAR';
import RemoveRequestDialog from './RemoveRequestDialog';
import { InfoCircleButton } from '../../../components/StyledButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLoading } from '../../../context/LoadingContext';

export const EVENT_STATUS = {
   checkInOut: 0,
   missingCheckIn: 1,
   missingCheckOut: 2,
};

const CustomToolbar = ({ onRefresh }) => {
   const { t } = useTranslation();
   const { profile, genaralSettingTimeZone } = useAuth();

   return (
      <GridToolbarContainer className='flex justify-between items-center mb-2'>
         <div className='flex items-center flex-wrap gap-3'>
            {/* <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            <Tooltip title={t('button.refresh-data')}>
               <Button className='justify-end' onClick={onRefresh}>
                  <ArrowRepeatIcon16pxSvg />
               </Button>
            </Tooltip> */}
         </div>

         <CustomGridToolbarQuickFilter fullWidth />
      </GridToolbarContainer>
   );
};

const RequestGridInfoTable = ({ isShowErrorLog, refreshData, onRowSelection, selectedDate, selectedShift, selectedUser, onRefresh, usersData, shiftsData = [], dispalyWeek }) => {
   const { genaralSettingDateFormat, genaralSettingTimeFormat, dateTimeFormat } = useAuth();
   const { t } = useTranslation();
   const { settings } = useSettings();
   const { profile, isAdmin, isEmployee, genaralSettingTimeZone } = useAuth();
   const { notifications } = useNotification();
   const language = useSelector((state) => state.languageR.language);
   const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
   const [rowData, setRowData] = useState([]);
   const [show, hide] = useLoading();
   const [page, setPage] = useState(0);
   const [pageSize, setPageSize] = useState(50);
   const [rowSelectData, setRowSelectData] = useState([]);
   const [rowCount, setRowCount] = useState(0)
   const [filteredRowData, setFilteredRowData] = useState([]);
   const [filteredRowCount, setFilteredRowCount] = useState(0);
   const [selectedRequest, setSelectedRequest] = useState(null);
   const [parsedNewData, setParsedNewData] = useState({});
   const [parsedOldData, setParsedOldData] = useState({});
   const [requestHistory, setRequestHistory] = useState([]);
   const [openRequestDetail, setOpenRequestDetail] = useState(false);
   const [openRequestDetailAR, setOpenRequestDetailAR] = useState(false);
   const [openEditRequestByUser, setOpenEditRequestByUser] = useState(false);
   const [openEditRequestEvent, setOpenEditRequestEvent] = useState(false);
   const [openRemoveRequestByUser, setOpenRemoveRequestByUser] = useState(false);

   const shiftsWithoutAll = useMemo(
      () => shiftsData.filter((shift) => shift.shiftId !== 0),
      [shiftsData]
   );

   const parseRequestData = useCallback((row) => {
      let newData = {};
      let oldData = {};
      try {
         if (row?.newData) newData = JSON.parse(row.newData);
         if (row?.oldData) oldData = JSON.parse(row.oldData);
      } catch (error) {
         console.error("Parse request data error:", error);
      }
      return { parsedNewData: newData, parsedOldData: oldData };
   }, []);

   const resetSelectedRequest = () => {
      setSelectedRequest(null);
      setParsedNewData({});
      setParsedOldData({});
      setRequestHistory([]);
   };

   const markNotificationRead = async (requestId) => {
      try {
         const role = isAdmin ? "ADMIN" : "USER";
         const updated = notifications.map((n) =>
            n.id === requestId ? { ...n, unread: false } : n
         );
         await updateNotification(updated, profile.userId, role, profile.companyId);
         onRefresh?.();
      } catch (error) {
         console.error(error);
      }
   };

   // Mở popup ngay, dialog tự fetch detail + history (giống LeaveAndHoliday)
   const openRequestDetailDialog = (row, openType = "PENDING") => {
      setSelectedRequest(row);
      const { parsedNewData: newData, parsedOldData: oldData } = parseRequestData(row);
      setParsedNewData(newData);
      setParsedOldData(oldData);
      setRequestHistory([]);

      if (openType === "PENDING") {
         setOpenRequestDetail(true);
      } else {
         setOpenRequestDetailAR(true);
      }
   };

   const canShowDetail = (row) =>
      isAdmin
          ? ["PENDING", "APPROVED", "REJECTED"].includes(row?.requestStatusName)
          : ["APPROVED", "REJECTED"].includes(row?.requestStatusName);

   const canShowEdit = (row) =>
      row?.requestStatusName === "PENDING" && row?.userId === profile?.userId;

   const canShowDelete = (row) =>
      row?.requestStatusName === "PENDING" && row?.userId === profile?.userId;

   // Mở popup trước, đánh dấu đã đọc chạy nền — không chờ mạng mới hiện dialog
   const handleDetailRequest = (row) => {
      if (row.requestStatusName === "PENDING") {
         openRequestDetailDialog(row, "PENDING");
      } else {
         openRequestDetailDialog(row, "AR");
      }
      markNotificationRead(row.id);
   };

   const handleEditRequest = (row) => {
      setSelectedRequest(row);
      const { parsedNewData: newData, parsedOldData: oldData } = parseRequestData(row);
      setParsedNewData(newData);
      setParsedOldData(oldData);

      if (row.requestTypeName === "EDIT_EVENT_LOG") {
         setOpenEditRequestByUser(true);
      } else {
         setOpenEditRequestByUser(true);
      }
   };

   const handleDeleteRequest = (row) => {
      setSelectedRequest(row);
      setOpenRemoveRequestByUser(true);
   };

   const handleCloseRequestDetail = () => {
      setOpenRequestDetail(false);
      resetSelectedRequest();
   };

   const handleCloseRequestDetailAR = () => {
      setOpenRequestDetailAR(false);
      resetSelectedRequest();
   };

   const handleCloseEditRequestByUser = () => {
      setOpenEditRequestByUser(false);
      resetSelectedRequest();
   };

   const handleCloseEditRequestEvent = () => {
      setOpenEditRequestEvent(false);
      resetSelectedRequest();
   };

   const handleCloseRemoveRequestByUser = () => {
      setOpenRemoveRequestByUser(false);
      resetSelectedRequest();
   };

   const filteredRows = useMemo(() => {
      if (isAdmin) return rowData;
      return rowData.filter(row => row.userId === profile.userId);
   }, [rowData, isAdmin, profile.userId]);

   const defaultColumnVisibility = {
      fullName: true,
      responseReason: true,
      requestReason: true,
      status: true,
      requestTypeName: true,
      reason: true,
      createdAt: true,
      createdBy: true,
      updatedBy: true
      // updatedAt: true,
      // updatedBy: true,
   };

   const [columnVisibilityModel, setColumnVisibilityModel] = useState(defaultColumnVisibility);
   const emailUser = profile?.email;
   const handleColumnVisibilityChange = (newModel) => {
      setColumnVisibilityModel(newModel);
      Cookies.set(`RequestGridInfoTable_${emailUser}`, JSON.stringify(newModel), {
         expires: 90,
         path: "/",
      });
   };

   useEffect(() => {
      const saved = Cookies.get(`RequestGridInfoTable_${emailUser}`);
      if (saved) {
         setColumnVisibilityModel(JSON.parse(saved));
      }
   }, []);

   function countVisibleColumns() {
      return (Object.values(columnVisibilityModel).filter(value => value === true).length) - 1;
   }

   const isFewColumns = countVisibleColumns() <= 5;

   const [allUser, setAllUser] = useState([]);

   useEffect(() => {
      const fetchUser = async () => {
         try {
            const payload = {
               statusId: 1,
               page: 1,
               pageSize: 1000000,
               status: null,
               langId: localStorage.getItem("i18nextLngId")
            }
            const response = await getUserList(payload);
            const usersList = response.data.data;
            setAllUser(usersList);
         } catch (error) {
            console.log(error)
         }
      };
      fetchUser();

   }, []);

   const getFullNameByEmail = (email) => {
      if (!email) return "-";

      const normalizedEmail = String(email).trim().toLowerCase();

      const user = allUser.find(
         (u) =>
            u.email &&
            u.email.trim().toLowerCase() === normalizedEmail
      );

      // nếu tìm được thì trả fullName, không thì fallback về email
      return user?.fullName || email;
   };

   const columnData = useMemo(
      () => [
         {
            field: 'fullName',
            headerName: t('common.fullname'),
            flex: 1,
            filterable: true,
         },
         {
            field: "requestStatusName",
            headerName: t("common.request-status"),
            hideable: true,
            flex: 1,
            valueGetter: (value) => {
               if (!value) return "-";

               switch (value) {
                  case "APPROVED":
                     return t("common.approved");
                  case "REJECTED":
                     return t("common.rejected");
                  case "PENDING":
                     return t("common.pending-request");
                  default:
                     return value;
               }
            },

            renderCell: (params) => {
               const rawStatus = params.row.requestStatusName;
               const label = params.value;

               let color = "#6B7280";

               switch (rawStatus) {
                  case "APPROVED":
                     color = "#22C55E";
                     break;
                  case "REJECTED":
                     color = "#EF4444";
                     break;
                  case "PENDING":
                     color = "#EAB308";
                     break;
               }

               return (
                  <div className="flex items-center gap-1.5">
                     <span style={{ color }}>
                        {label}
                     </span>
                  </div>
               );
            },
         },
         {
            field: "requestTypeName",
            headerName: t("common.type"),
            hideable: true,
            flex: 1,
            // 🔥 thêm cái này để filter/search theo label
            valueGetter: (value) => {
               if (!value) return "-";

               return t(`requestType.${value}`, {
                  defaultValue: value.replace(/_/g, " "),
               });
            },

            rowSpanValueGetter: (_, row) => {
               if (!row.requestTypeName) return "";

               return t(`requestType.${row.requestTypeName}`, {
                  defaultValue: row.requestTypeName.replace(/_/g, " "),
               });
            },

            renderCell: ({ value }) => <span>{value}</span>,
         },
         {
            field: 'requestReason',
            headerName: t('common.requestReason'),
            hideable: true,
            flex: 1,
            rowSpanValueGetter: (value, row) => (row ? `${row.id}-${row.requestReason}` : value),
            valueFormatter: (value) => {
               return value === null || value === undefined ? "No sensor data" : `${value}`;
            },
            // valueGetter: (params) => (params.row.requestReason ? params.row.requestReason : ''),
         },
         {
            field: 'responseReason',
            headerName: t('common.responseReason'),
            hideable: true,
            flex: 1,
            rowSpanValueGetter: (value, row) => (row ? `${row.id}-${row.responseReason}` : value),
            valueFormatter: (value) => {
               return value === null || value === undefined ? "-" : `${value}`;
            },
            // valueGetter: (params) => (params.row.responseReason ? params.row.responseReason : ''),
         },
         {
            field: 'createdTime',
            headerName: t('common.created-time'),
            hideable: true,
            flex: 1,
            rowSpanValueGetter: (value, row) => (row ? `${row.id}-${row.createdTime}` : value),
            valueFormatter: (value) => {
               if (value === null || value === undefined) return "-";

               // Sử dụng date-fns để chuyển đổi định dạng
               const formattedDate = value
                  ? format(new Date(value), dateTimeFormat || "dd/MM/yyyy HH:mm:ss")
                  : "";

               return formattedDate;
            },
         },
         {
            field: "modifiedBy",
            headerName: t("common.approve-reject-by"),
            hideable: true,
            flex: 1,

            valueGetter: (value) => {
               if (!value) return t("common.not-processed-yet");

               return getFullNameByEmail(value) || value;
            },

            renderCell: (params) => (
               <span>
                  {params.value || t("common.not-processed-yet")}
               </span>
            ),
         },
         {
            field: "option",
            headerName: t("common.options"),
            width: 140,
            minWidth: 140,
            hideable: false,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "left",
            headerAlign: "left",
            renderCell: (params) => {
               const row = params.row;
               const showDetail = canShowDetail(row);
               const showEdit = canShowEdit(row);
               const showDelete = canShowDelete(row);

               if (!showDetail && !showEdit && !showDelete) {
                  return null;
               }

               const iconBtnClass =
                  "inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-lg hover:bg-gray-100 transition cursor-pointer p-0";

               return (
                  <div
                     className="flex flex-nowrap items-center justify-start gap-1 h-full w-full"
                     onClick={(e) => e.stopPropagation()}
                  >
                     {showDetail && (
                        <Tooltip title={t("button.details")} arrow>
                           <button
                              type="button"
                              className={iconBtnClass}
                              onClick={() => handleDetailRequest(row)}
                           >
                              <InfoOutlinedIcon
                                 sx={{ fontSize: 20 }}
                                 className="text-[#0057FF]"
                              />
                           </button>
                        </Tooltip>
                     )}
                     {showEdit && (
                        <Tooltip title={t("button.edit")} arrow>
                           <button
                              type="button"
                              className={iconBtnClass}
                              onClick={() => handleEditRequest(row)}
                           >
                              <EditNew20pxSvg width={20} height={20} />
                           </button>
                        </Tooltip>
                     )}
                     {showDelete && (
                        <Tooltip title={t("button.delete")} arrow>
                           <button
                              type="button"
                              className={`${iconBtnClass} hover:bg-red-100`}
                              onClick={() => handleDeleteRequest(row)}
                           >
                              <DeleteIcon16pxSvg width={20} height={20} color="#D00D00" />
                           </button>
                        </Tooltip>
                     )}
                  </div>
               );
            },
         }
      ],
      [settings.dateFormat, settings.timeFormat, t, columnVisibilityModel, allUser, isAdmin, profile?.userId]
   );

   const getFromToByDateWithTZ = (date, timeZone) => {
      const base = dayjs(date).utcOffset(timeZone);

      return {
         from: base.startOf("day").utc().toISOString(),
         to: base.endOf("day").utc().toISOString(),
      };
   };

   const getUTCFromToByDate = (date) => {
      const from = new Date(Date.UTC(
         date.getFullYear(),
         date.getMonth(),
         date.getDate(),
         0, 0, 0
      )).toISOString();

      const to = new Date(Date.UTC(
         date.getFullYear(),
         date.getMonth(),
         date.getDate(),
         23, 59, 59
      )).toISOString();

      return { from, to };
   };

   useEffect(() => {
      const fetchData = async () => {
         const browserTZ = dayjs.tz.guess();

         const { from, to } = getFromToByDateWithTZ(
            selectedDate,
            genaralSettingTimeZone || "+00:00"
         );


         const payload = {
            page: page + 1,
            record: pageSize,
            from,
            to,
            userId: isAdmin ? selectedUser?.id : profile.userId,
            langId: localStorage.getItem("i18nextLngId"),
            requestType: "EVENT"
         }
         try {
            show();
            const response = await getRequestList(payload)
            const requestsList = response.data.data;

            const firstRow = requestsList[0];
            setRowData(requestsList);
            setRowCount(response.data.pagination.totalElements)
            setFilteredRowData(requestsList);
            setFilteredRowCount(requestsList.length);
            if (firstRow && rowSelectData.length === 0) {
               onRowSelection(firstRow);
            } else {
               onRowSelection({});
            }
         } catch (error) {
            if (error.response.data) {
               if (error.response.data.responseCode !== 401) {
                  toast.error(error.response.data.message);
               }
            } else {
               toast.error(t('common.network-timeout'));
            }
         } finally {
            hide();
         }
      };

      fetchData();
   }, [
      isShowErrorLog,
      selectedDate,
      selectedUser,
      selectedShift,
      page,
      pageSize,
      language,
      profile,
      isAdmin,
      t,
      refreshData,
      onRowSelection,
   ]);

   useEffect(() => {
      setRowSelectData([]);
   }, [selectedDate, selectedUser, selectedShift]);

   const onRowsSelectionHandler = (selectedRowIds) => {
      const selectedRows = rowData.filter(row => selectedRowIds.includes(row.id));

      if (selectedRows.length) {
         onRowSelection(selectedRows[0]);
      }

      setRowSelectData(selectedRows)

   };

   const handleQuickFilterChange = (newModel) => {
      const searchText = newModel.quickFilterValues?.[0] || '';

      if (!searchText.trim()) {
         setFilteredRowData(rowData);
         setFilteredRowCount(rowData.length);
         return;
      }

      const lowerSearch = searchText.trim().toLowerCase();

      const filtered = rowData.filter(row =>
         Object.values(row).some(val =>
            String(val ?? '').toLowerCase().includes(lowerSearch)
         )
      );

      setFilteredRowData(filtered);
      setFilteredRowCount(filtered.length);
   };

   const hasRows = filteredRowData.length > 0;

   const gridHeight = hasRows ? 500 : 360;

   return (
      <>
         <div className="w-full min-w-0 max-w-full overflow-hidden">
            <div
               className={`w-full max-w-full ${hasRows ? "overflow-x-auto" : "overflow-x-hidden"}`}
            >
               <StyledDataGrid
            // rows={rowData}
            rows={filteredRowData}
            rowCount={filteredRowCount}
            paginationMode="client"
            ignoreDiacritics
            sx={{
               width: "100%",
               maxWidth: "100%",
               overflow: "hidden",
               height: gridHeight,

               "& .MuiDataGrid-root": {
                  width: "100%",
                  maxWidth: "100%",
               },
               "& .MuiDataGrid-main": {
                  width: "100%",
                  maxWidth: "100%",
                  overflow: "hidden",
               },
               "& .MuiDataGrid-columnHeadersInner": {
                  width: "100%"
               },
               "& .MuiDataGrid-scrollbar--vertical": {
                  display: "none !important",
               },
               "& .MuiDataGrid-scrollbar--horizontal": {
                  display: hasRows ? undefined : "none !important",
               },

               "& .MuiDataGrid-scrollbarFiller": {
                  display: "none !important",
               },
               "& .MuiDataGrid-columnHeaders": {
                  paddingRight: 0,
               },
               "& .MuiDataGrid-topContainer": {
                  paddingRight: 0,
               },

               "&": {
                  "--DataGrid-scrollbarSize": "0px",
               },

               "& .MuiDataGrid-virtualScroller": {
                  overflow: hasRows ? "auto !important" : "hidden !important",
                  scrollbarWidth: "none",
               },
               "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                  width: 0,
                  height: 0,
               },
               ...(hasRows && {
                  "&:hover .MuiDataGrid-virtualScroller, &:focus-within .MuiDataGrid-virtualScroller": {
                     scrollbarWidth: "thin",
                  },
                  "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar, &:focus-within .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                     width: 10,
                     height: 10,
                  },
               }),

               '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
                  display: hasRows ? undefined : "none !important",
               },

               "& input[type=number]::-webkit-inner-spin-button, \
 & input[type=number]::-webkit-outer-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
               },

               "& input[type=number]": {
                  textAlign: "center",
                  MozAppearance: "textfield",
                  WebkitAppearance: "none",
                  padding: "5px !important",
                  height: "100%",
                  pointerEvents: "none",
                  caretColor: "transparent",
                  userSelect: "none",
                  paddingTop: "8px !important",
                  paddingBottom: "8px !important"
               },
            }}
            columns={columnData}
            getRowId={(r) => r.id}
            slots={{
               // toolbar: CustomToolbar,
               pagination: CustomPagination,
               noRowsOverlay: NoRowOverlay,
               columnsPanel: CustomColumnsPanel,
            }}
            slotProps={{
               toolbar: {
                  showQuickFilter: true,
                  onRefresh,
               },
               // noRowsOverlay: {
               //    title: t('common.content-122'),
               // },
               pagination: {
                  page,
                  count: filteredRowCount,
                  rowsPerPage: pageSize,
                  onPageChange: setPage,
                  onRowsPerPageChange: setPageSize,
               },
            }}
            localeText={tableTranslated}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            onRowSelectionModelChange={onRowsSelectionHandler}
         // localeText={useMemo(() => generateTranslateMuiTable(t), [t])}
         />
            </div>
         </div>

         {openRequestDetail && selectedRequest && (
            <DetailRequestDialog
               onClose={handleCloseRequestDetail}
               usersData={usersData}
               shiftsData={shiftsWithoutAll}
               onRefresh={onRefresh}
               info={selectedRequest}
               tabName={1}
               parsedNewData={parsedNewData}
               parsedOldData={parsedOldData}
               requestHistory={requestHistory}
            />
         )}
         {openRequestDetailAR && selectedRequest && (
            <DetailRequestDialogAR
               onClose={handleCloseRequestDetailAR}
               usersData={usersData}
               shiftsData={shiftsWithoutAll}
               onRefresh={onRefresh}
               info={selectedRequest}
               tabName={1}
               parsedNewData={parsedNewData}
               parsedOldData={parsedOldData}
               requestHistory={requestHistory}
            />
         )}
         {openEditRequestByUser && selectedRequest && (
            <EditRequestDialogByUser
               onClose={handleCloseEditRequestByUser}
               usersData={usersData}
               shiftsData={shiftsWithoutAll}
               onRefresh={onRefresh}
               info={selectedRequest}
               tabName={1}
               parsedNewData={parsedNewData}
               parsedOldData={parsedOldData}
            />
         )}
         {openEditRequestEvent && selectedRequest && (
            <EditRequestEventDialogByUser
               onClose={handleCloseEditRequestEvent}
               usersData={usersData}
               shiftsData={shiftsWithoutAll}
               onRefresh={onRefresh}
               info={selectedRequest}
               tabName={1}
               parsedNewData={parsedNewData}
               parsedOldData={parsedOldData}
            />
         )}
         {openRemoveRequestByUser && selectedRequest && (
            <RemoveRequestDialog
               onClose={handleCloseRemoveRequestByUser}
               onRefresh={onRefresh}
               refreshData={refreshData}
               info={selectedRequest}
            />
         )}
      </>
   );
};

export default RequestGridInfoTable;
